export const MIN_PLAUSIBLE_FLAT_RATE_EUROS = 20;

/**
 * Check if the external comment contains a URL (http://, https://, or www. prefix).
 */
export const externalCommentContainsUrl = (comment: string): boolean => {
  const urlPattern = /https?:\/\/|www\./i;
  return urlPattern.test(comment);
};

/**
 * Check if the external comment contains the customer's name as a whole word (case-insensitive).
 * Either the first name OR the last name matching triggers it (not both required).
 * Names with regex-special characters are escaped.
 * Empty/null/undefined names must never match.
 */
export const externalCommentContainsCustomerName = (
  comment: string,
  customer: { firstName?: string | null; lastName?: string | null }
): boolean => {
  const { firstName, lastName } = customer;

  // Empty/null/undefined names must never match
  const names = [firstName, lastName].filter(
    (name): name is string => typeof name === "string" && name.trim().length > 0
  );

  if (names.length === 0) {
    return false;
  }

  // Normalize to NFC so a precomposed name (José) and a decomposed comment
  // (Jose + combining acute), or vice versa, compare as the same text.
  const normalizedComment = comment.normalize("NFC");

  // Check each name as a whole word (case-insensitive)
  for (const name of names) {
    // Escape special regex characters
    const escapedName = name
      .trim()
      .normalize("NFC")
      .replace(/[.*+?^${}()|[\]\\]/g, "\\$&");
    // Match whole word: Unicode-aware boundary (\b is ASCII-only and misses
    // names starting/ending in ö/ü/ß/é, common in German customer names).
    const namePattern = new RegExp(
      `(?<![\\p{L}\\p{M}\\p{N}_])${escapedName}(?![\\p{L}\\p{M}\\p{N}_])`,
      "iu"
    );
    if (namePattern.test(normalizedComment)) {
      return true;
    }
  }

  return false;
};

/**
 * Check if a flat rate amount is implausibly low (potential typo).
 * Returns true only for amounts strictly between 0 (exclusive) and 20 (exclusive).
 * Exactly 20 or higher must not trigger it.
 * Zero/negative amounts must not trigger it (they'll fail existing validation).
 */
export const isImplausiblyLowFlatRate = (flatRateEuros: number): boolean => {
  return flatRateEuros > 0 && flatRateEuros < MIN_PLAUSIBLE_FLAT_RATE_EUROS;
};
